import store from '@/store'
import axios from 'axios'
import heic2any from 'heic2any'
import Cookies from 'js-cookie'

const MAX_IMAGE_SIZE = 960
const IMAGE_QUALITY = 1.0

export const debounce = function debounce(func, wait, immediate) {
  var timeout
  return function() {
    var context = this, args = arguments
    var later = function() {
      timeout = null
      if (!immediate) func.apply(context, args)
    };
    var callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

export const logEvent = (name, count = 0, duration = 0, params = {}) => {
  if (typeof count === 'object') {
    params = count
    count = 0
  } else if (typeof duration === 'object') {
    params = duration
    duration = 0
  }

  if (process.env.VUE_APP_ENV === 'test') {
    let paramsDisplay = ''
    Object.keys(params).forEach(k => {
      paramsDisplay += `${k}=${params[k]} `
    })
    console.log(`[ANALYTICS] logEvent: web.${name} ${paramsDisplay}`)
  }
  let data = {
    name: 'web.' + name,
    device_id: store.state.user.deviceId,
    user_id: store.state.user.user ? store.state.user.user.id : 0,
    company_id: store.state.company.company ? store.state.company.company.id : 0,
    count: count,
    duration: duration,
    params: params
  }
  axios.post(`${process.env.VUE_APP_ANALYTICS_URL}/log`, data)
}

/**
 * Resize image from a file. Default max size is 960px defined at MAX_IMAGE_SIZE.
 * @param file - File of the image.
 * @param name - Name for the resized image.
 * @return Resized image with dataURL & file.
 */
export const resizeImage = async (file, name) => {
  let preprocessFile = file
  if (file.type === 'image/heic') {
    preprocessFile = await convertHeicToJpeg(file, name)
  }
  return new Promise(resolve => {
    const fileReader = new FileReader()
    fileReader.readAsDataURL(preprocessFile)
    fileReader.onload = e => {
      const image = new Image()
      image.src = e.target.result
      image.onload = () => {
        const canvas = document.createElement('canvas')
        const width = image.width
        const height = image.height
        let resizeFactor = 1.0
        if (width > MAX_IMAGE_SIZE || height > MAX_IMAGE_SIZE) {
          if (height > width) {
            resizeFactor = MAX_IMAGE_SIZE / height
          } else {
            resizeFactor = MAX_IMAGE_SIZE / width
          }
        }
        const resizeWidth = width * resizeFactor
        const resizeHeight = height * resizeFactor
        canvas.width = resizeWidth
        canvas.height = resizeHeight
        canvas.getContext('2d').drawImage(image, 0, 0, resizeWidth, resizeHeight)
        const dataURL = canvas.toDataURL('image/jpeg')
        canvas.toBlob((blob) => {
          const file = new File([blob], name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          })
          resolve({ dataURL, file })
        }, 'image/jpeg', IMAGE_QUALITY)
      }
    }
  })
}

const convertHeicToJpeg = async (file, name) => {
  const blob = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: IMAGE_QUALITY
  })
  return new File([blob], name)
}

export const setupLanguage = () => {
  const key = "yz_language"
  let domain = `.${process.env.VUE_APP_BASE_DOMAIN}`
  let secure = true
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    domain = window.location.hostname
    secure = false
  }
  if (!Cookies.get(key)) {
    Cookies.set(key, 'en', { secure, domain, expires: 90 })
    if (isIndonesiaTimeZone()) {
      Cookies.set(key, 'id', { secure, domain, expires: 90 })
    } else {
      const browserLanguage = getBrowserLanguage()
      if (browserLanguage === 'id') {
        Cookies.set(key, 'id', { secure, domain, expires: 90 })
      }
    }
  }
}

const isIndonesiaTimeZone = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const idZones = ['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura', 'Asia/Pontianak']
  return idZones.includes(timeZone)
}

const getBrowserLanguage = () => {
  const nav = window.navigator
  const browserLanguagePropertyKeys = ['language', 'browserLanguage', 'systemLanguage', 'userLanguage']
  let i
  let language

  if (Array.isArray(nav.languages)) {
    for (i = 0; i < nav.languages.length; i++) {
      language = nav.languages[i]
      if (language && language.length) {
        return language
      }
    }
  }

  for (i = 0; i < browserLanguagePropertyKeys.length; i++) {
    language = nav[browserLanguagePropertyKeys[i]]
    if (language && language.length) {
      return language
    }
  }

  return null
}